import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import _cloneDeep from 'lodash/cloneDeep';
import _find from 'lodash/find';
import _findIndex from 'lodash/findIndex';
import _isUndefined from 'lodash/isUndefined';
import _throttle from 'lodash/throttle';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { StickyContainer } from 'react-sticky';
import Helmet from 'react-helmet';
import { Footer } from '@zola/zola-ui/src/components/Footer';
import queryString from 'query-string';
import cx from 'classnames';
import { RegistryTileGrid } from '@zola/component-public-registry';
import { trackEvent, trackProductAdded, trackProductListFiltered } from '../../util/trackingHelper';
/* Actions */
import { addItem } from '../../actions/CartActions';
import { showModal, hideModal } from '../../actions/ModalActions';
import {
  getRegistryProducts,
  toggleFacetValue,
  resetAllFacetValue,
  resetFacetValue,
} from '../../actions/RegistryCollectionActions';
import { onIdentifyGuestUser } from '../../actions/UserActions';
import { requestRegistryInfoBySlug } from '../../actions/RegistryActions';
import { requestProductByLookKey } from '../../actions/ProductActions';
/* Components */
import UnifiedNav from '../../components/common/header/UnifiedNav/UnifiedNav';
import RegistryHeader from '../../components/registry/RegistryCollectionItem/RegistryHeader';

import FeatureFlags from '../../util/featureFlags';
import experimentFlags, {
  REGISTRY_GIFT_GIVER_TILE_HEIGHT,
  REGISTRY_GIFT_GIVER_PDP_BADGE,
} from '../../util/experimentFlags';
import { isMobile } from '../../lib/responsive';

import './PublicRegistry.less';

const PUBLIC_REGISTRY_EVENT = 'PUBLIC_REGISTRY_EVENT';
const OPEN_MODAL_EVENT = 'OPEN_MODAL_EVENT';
const GIFT_CARD_LOOK_KEY = 'zola_gift_card';
const GIFT_CARD_TYPE = 'GIFT_CARD';
const SHOW_SCROLL_BUTTON_Y_BREAK = 500;

class PublicRegistry extends Component {
  static getDerivedStateFromProps(props, state) {
    if (state.completeFacets.length === 0) {
      return {
        completeFacets: props.facets,
      };
    }

    // Return null if the state hasn't changed
    return null;
  }

  /**
   * Notify web-nav a user landed on Public Registry page
   */
  static dispatchEnterEvent() {
    window.dispatchEvent(new CustomEvent(PUBLIC_REGISTRY_EVENT, { detail: 'ENTER' }));
  }

  /**
   * Notify web-nav a user existed Public Registry page
   */
  static dispatchExitEvent() {
    window.dispatchEvent(new CustomEvent(PUBLIC_REGISTRY_EVENT, { detail: 'EXIT' }));
  }

  static scrollToTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  static propTypes = {
    defaultCollection: PropTypes.arrayOf(PropTypes.shape({})),
    defaultGiftCard: PropTypes.shape({}),
    busy: PropTypes.bool,
    otherCollections: PropTypes.arrayOf(PropTypes.shape({})),
    collectionList: PropTypes.arrayOf(PropTypes.shape({})),
    selectedFacetValues: PropTypes.shape({}),
    registry: PropTypes.shape({}),
    facets: PropTypes.arrayOf(PropTypes.shape({})),
    modalInfo: PropTypes.shape({}),
    match: PropTypes.shape({
      params: PropTypes.shape({
        registryKey: PropTypes.string,
      }),
    }),
    handleRegistrySearch: PropTypes.func,
    handleFacetValueChange: PropTypes.func,
    handleAllFacetValueReset: PropTypes.func,
    handleFacetValueReset: PropTypes.func,
    handleHideModal: PropTypes.func,
    requestRegistryInfo: PropTypes.func,
    fetchProductByLookKey: PropTypes.func,
    handleAddItem: PropTypes.func,
    handleModal: PropTypes.func,
    user: PropTypes.shape({}),
    history: PropTypes.shape({}),
    handleGuestVisit: PropTypes.func,
  };

  static defaultProps = {
    defaultCollection: [],
    collectionList: [{ object_id: null, key: null, title: 'All Categories' }],
  };

  constructor(props) {
    super(props);
    this.selectCategory = this.selectCategory.bind(this);
    this.selectFacet = this.selectFacet.bind(this);
    this.selectSortBy = this.selectSortBy.bind(this);
    this.addToCart = this.addToCart.bind(this);
    this.handleCategoryTagClick = this.handleCategoryTagClick.bind(this);
    this.handleClearFacet = this.handleClearFacet.bind(this);
    this.handleProductClick = this.handleProductClick.bind(this);
    this.handleClearAllAction = this.handleClearAllAction.bind(this);
    this.getDefaultCollectionItems = this.getDefaultCollectionItems.bind(this);
    this.setScrollButtonVisibility = this.setScrollButtonVisibility.bind(this);
    this.state = {
      completeFacets: [],
      shouldRenderPage: false,
      resetSort: false,
      currentCategoryObj: {},
    };

    this.setVisibility = _throttle(this.setScrollButtonVisibility, 500);
    this.scrollButton = React.createRef();
  }

  /**
   * Handle initial search with registry key provided from URL
   */
  componentDidMount() {
    // Disable secondary nav - DO THIS RIGHT AWAY
    window.zolaNav = window.zolaNav || {};
    window.zolaNav.secondaryNavType = '';
    if (window.zolaNav.setSecondaryNavType) {
      window.zolaNav.setSecondaryNavType('');
    }
    const {
      handleRegistrySearch,
      fetchProductByLookKey,
      requestRegistryInfo,
      match,
      handleGuestVisit,
      handleHideModal,
    } = this.props;
    const { registryKey } = match.params;

    requestRegistryInfo(registryKey).then(
      () => {
        // Identify Guest Users to public registries - do this right away
        handleGuestVisit();

        const { user, registry } = this.props;
        const isOwnRegistry =
          user.registry_ids && user.registry_ids.indexOf(registry.registry_object_id) > -1;
        const canViewRegistry = registry.public || isOwnRegistry;
        const query = window.location.search;
        const isCouplePreview = queryString.parse(query).preview === 'true';
        const isAdminPreview = queryString.parse(query).admin_preview === 'true';

        // inject noindex tag if registry is non-searchable
        if (registry.searchable_on_web === false) {
          this.injectNoIndexTag();
        }

        if (isOwnRegistry && !isCouplePreview) {
          this.redirectToEditMode();
        } else if (!canViewRegistry && !isAdminPreview) {
          this.redirectToNotFound();
        } else {
          this.setState({
            shouldRenderPage: true,
          });
          // fetch gift card information if enabled
          if (registry.default_gift_card_enabled) {
            fetchProductByLookKey(GIFT_CARD_LOOK_KEY);
          }
          // fetch registry items
          const config = {
            registryKey,
            groupedByCollection: registry.enable_custom_collections,
            flattenedView: !registry.enable_custom_collections,
          };
          handleRegistrySearch(config);

          // Track registry viewed for guests to this registry
          if (!isOwnRegistry) {
            this.trackRegistryPageView(registry.registry_object_id);
          }
        }
      },
      () => {
        // redirect if registry doesn't exist
        this.redirectToNotFound();
      }
    );

    // fire event after user scroll beyond breaking point
    window.addEventListener('scroll', this.setVisibility);
    // fire event after unified nav is mounted
    window.addEventListener('unified-nav-mounted', PublicRegistry.dispatchEnterEvent);
    // fire event after back button is click
    window.addEventListener('popstate', handleHideModal);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      handleRegistrySearch,
      selectedFacetValues,
      match: {
        params: { registryKey },
      },
      registry: { enable_custom_collections: enableCustomCollections },
    } = this.props;
    const { sort, currentCategoryObj } = this.state;

    if (
      selectedFacetValues !== prevProps.selectedFacetValues ||
      sort !== prevState.sort ||
      currentCategoryObj !== prevState.currentCategoryObj
    ) {
      /*
       * Filters/sort results handling
       */
      let config = {
        registryKey,
        filters: selectedFacetValues,
        collectionObjectId: currentCategoryObj?.value || null,
        groupedByCollection: enableCustomCollections,
        flattenedView: !enableCustomCollections,
      };

      if (sort && sort !== 'FEATURED') {
        config = {
          ...config,
          sort,
        };
      }
      handleRegistrySearch(config);

      /*
       * Tracking handling
       */
      // Aggregate Filtering values
      const filters = [];
      // eslint-disable-next-line no-unused-vars
      for (const type in selectedFacetValues) {
        if (Object.getOwnPropertyDescriptor(selectedFacetValues, type)) {
          selectedFacetValues[type].forEach((value) => {
            filters.push({
              type,
              value,
            });
          });
        }
      }

      // Aggregate Category value
      if (currentCategoryObj) {
        filters.push({
          type: 'CATEGORY',
          value: currentCategoryObj.label,
        });
      }

      trackProductListFiltered({
        filters,
        sorts: [
          {
            type: 'SORT',
            value: sort?.value || 'FEATURED',
          },
        ],
      });
    }
  }

  /**
   * remove listeners
   */
  componentWillUnmount() {
    const { handleHideModal } = this.props;
    window.removeEventListener('unified-nav-mounted', PublicRegistry.dispatchEnterEvent);
    window.removeEventListener('scroll', this.setScrollButtonVisibility);
    window.removeEventListener('popstate', handleHideModal);
  }

  setScrollButtonVisibility() {
    // make sure scroll button is there before change the style
    if (window.pageYOffset > SHOW_SCROLL_BUTTON_Y_BREAK) {
      if (this.scrollButton && this.scrollButton.current) {
        this.scrollButton.current.style.opacity = 1;
      }
    } else if (this.scrollButton && this.scrollButton.current) {
      this.scrollButton.current.style.opacity = 0;
    }
  }

  /**
   * Get collection items with or without default gift card, depending on settings
   * @returns {Array<Object>} - array of collection products (with optional default gift card)
   */
  getDefaultCollectionItems() {
    const { defaultCollection, defaultGiftCard, registry } = this.props;
    if (registry.default_gift_card_enabled) {
      const collection = _cloneDeep(defaultCollection);
      let index = _findIndex(
        collection,
        (item) =>
          (item.contributions &&
            (item.contributions.show_as_fulfilled || item.contributions.show_as_reserved)) ||
          (item.actions && !item.actions.add_to_cart)
      );
      // if there's no unavailable for purchase items, place it in the end of the collection
      if (index < 0) {
        index = collection.length;
      }

      collection.splice(index, 0, defaultGiftCard);
      return collection;
    }
    return defaultCollection;
  }

  /**
   * create dropdown options for category
   * @returns {{key: string, label: string, options: {value: string, label: string}[]}}
   */
  getCategoryOptions() {
    const { collectionList, registry } = this.props;

    const collectionConfig = registry.enable_custom_collections
      ? {
          title: 'All Collections',
          key: 'COLLECTIONS',
          label: 'Collections',
        }
      : {
          title: 'All Categories',
          key: 'CATEGORIES',
          label: 'Categories',
        };
    // Clear category selection
    const defaultObject = { object_id: null, key: null, title: collectionConfig.title };
    return {
      key: collectionConfig.key,
      label: collectionConfig.label,
      options: [defaultObject, ...collectionList].map((item) => ({
        value: item.object_id,
        label: item.title,
      })),
    };
  }

  /**
   * create dropdown options for sort by
   * @returns {{key: string, label: string, options: *[]}}
   */
  getSortOptions() {
    return {
      key: 'SORT_BY',
      label: 'Sort By',
      options: [
        {
          value: null,
          label: 'Featured',
        },
        {
          value: 'MOST_WANTED',
          label: "Couple's Most Wanted",
        },
        {
          value: 'PRICE_ASC',
          label: 'Price: Low to High',
        },
        {
          value: 'PRICE_DESC',
          label: 'Price: High to Low',
        },
      ],
    };
  }

  /**
   * merge all dropdown options
   * @returns {{key: string, label: string, options: *[]}[]}
   */
  getDropdownOptions() {
    const { facets } = this.props;
    facets.forEach((facet) => {
      if (!_find(facet.options, { label: 'All' })) {
        facet.options.unshift({
          value: '',
          label: 'All',
        });
      }
    });
    return [this.getCategoryOptions(), ...facets, this.getSortOptions()];
  }

  redirectToEditMode() {
    const { history } = this.props;
    window.location.href = `${window.location.origin}${history.location.pathname}/edit`;
  }

  injectNoIndexTag() {
    const noindexTag = document.createElement('meta');
    noindexTag.content = 'noindex';
    noindexTag.name = 'robots';
    document.head.appendChild(noindexTag);
  }

  redirectToNotFound() {
    const { history } = this.props;
    history.push('/notfound');
  }

  /**
   * handle click event for category tags. Selected Category/Facet will be unselected
   * NOTE: unselected category will reset all dropdown
   */
  handleCategoryTagClick() {
    const { handleRegistrySearch, handleAllFacetValueReset, match, registry } = this.props;
    const { registryKey } = match.params;
    handleRegistrySearch({
      collectionObjectId: null,
      registryKey,
      groupedByCollection: registry.enable_custom_collections,
      flattenedView: !registry.enable_custom_collections,
    });
    handleAllFacetValueReset();
  }

  /**
   * callback function triggered by facet clear click event.
   * @param key
   */
  handleClearFacet(key) {
    const { handleFacetValueReset } = this.props;
    handleFacetValueReset(key);
  }

  /**
   * Using browser History API to change the URL without redirecting user to the page.
   */
  handleBrowserHistory() {
    const { modalInfo, match, history } = this.props;
    const { enablePushState } = this.state;
    if (enablePushState) {
      if (!modalInfo.modalType) {
        window.history.pushState(
          null,
          '',
          `/registry/${match.params.registryKey}${history.location.search}`
        );
      }
    }
    setTimeout(PublicRegistry.dispatchEnterEvent, 500);
  }

  handleProductClick(collectionItem, position) {
    const { handleModal, match } = this.props;
    const isDefaultGiftCard = collectionItem.type === GIFT_CARD_TYPE;
    const collectionItemId = collectionItem.object_id;
    const newUrl = isDefaultGiftCard
      ? `/registry/${match.params.registryKey}/zola-gift-card`
      : `/registry/collection-item/${collectionItemId}`;
    handleModal(
      'PRODUCT',
      {
        collectionItemId,
        registryKey: match.params.registryKey,
        collectionItem,
        position,
      },
      { size: 'xl' }
    );

    // Enable Changing URL without routing the user
    this.setState({
      enablePushState: true,
    });
    window.history.pushState(null, '', newUrl);
  }

  handleClearAllAction() {
    const { handleAllFacetValueReset } = this.props;
    this.setState({
      currentCategoryObj: null,
      sort: null,
      resetSort: true,
    });
    handleAllFacetValueReset();
  }

  /**
   * callback function triggered by facet dropdown click event.
   * @param options
   */
  selectCategory(options) {
    const { handleAllFacetValueReset } = this.props;
    this.setState({
      currentCategoryObj: options,
    });
    handleAllFacetValueReset();
  }

  /**
   * callback function triggered by price facet dropdown click event.
   * @param options
   * @param isSingleSelect
   */
  selectFacet(options, isSingleSelect = false) {
    const { handleFacetValueChange, selectedFacetValues } = this.props;
    const facetOption = Object.assign({}, options);
    // if single selection is required, unset the selected facet first
    if (isSingleSelect) {
      if (selectedFacetValues[options.facetKey]) {
        handleFacetValueChange({
          facetKey: options.facetKey,
          valueKey: selectedFacetValues[options.facetKey][0],
        });
      }
    }
    handleFacetValueChange(facetOption);
  }

  /**
   * callback function triggered by sort dropdown click event.
   * @param value
   */
  selectSortBy({ value }) {
    this.setState({
      sort: value,
      resetSort: false,
    });
  }

  /**
   * Creates payload object for custom event from product and supplemental data
   * @param {Object} product - registry collection item
   * @param {Number} quantity - amount of a product
   * @param {String} cartId - cart id to which product is being added to.
   * @returns {Object} - complete payload for custom event tracking
   */
  buildCustomEventData(product, quantity, cartId) {
    const { registry } = this.props;
    const registryId = registry.registry_object_id;
    const sku = product.sku_object_id;
    const productId = product.product_id;
    const productLookId = product.product_look_id;
    const { name, price } = product;
    const brand = product.brand && product.brand.name;
    const imageUrl = product.images[0].medium;
    return {
      cart_id: cartId,
      registry_id: registryId,
      product_id: productId,
      sku,
      name,
      brand,
      price,
      quantity,
      image_url: imageUrl,
      variant: productLookId,
      location: 'PUBLIC_REGISTRY',
      section: 'QUICK_ADD',
    };
  }

  addToCart(product, quantity, isEntireGift) {
    const collectionItemId = product.object_id;
    const skuId = product.sku_object_id;
    const addToCartRequest = isEntireGift
      ? {
          collectionItemId,
          skuId,
          quantity: 1,
          buyGroupGift: true,
        }
      : {
          collectionItemId,
          skuId,
          quantity,
          itemOrigin: 'WEDDING_REGISTRY',
        };
    const { handleAddItem } = this.props;
    handleAddItem(addToCartRequest)
      .then((result) => {
        const { cartId } = result.data;
        const eventData = this.buildCustomEventData(product, quantity, cartId);
        trackProductAdded(eventData);
        window.dispatchEvent(new CustomEvent(OPEN_MODAL_EVENT, { detail: 'CART' }));
      })
      .catch((error) => {
        window.dispatchEvent(
          new CustomEvent(`UPDATE_MAX_QUANTITY_${collectionItemId}`, { detail: error })
        );
      });
  }

  /**
   * Track a Registry Viewed event for a user visiting the registry if it is not the owner
   * @registryId registryId of the current public registry
   */
  trackRegistryPageView(registryId) {
    const data = {
      location: 'REGISTRY_STANDALONE',
      registry_id: registryId,
    };

    trackEvent('Registry Viewed', data);
  }

  renderRegistryHeader() {
    const { registry } = this.props;
    return <RegistryHeader registryInfo={registry} />;
  }

  renderSEOTags() {
    const { registry } = this.props;
    const couplesNames = `${registry.registrant_first_name} ${registry.registrant_last_name} and ${registry.partner_registrant_first_name} ${registry.partner_registrant_last_name}`;
    const defaultMetaDescription = `Check out ${couplesNames}'s Wedding Registry on Zola. Browse their gift selections now!`;
    return (
      <Helmet defaultTitle={`${couplesNames}'s Wedding Registry on Zola - Zola`}>
        {/* SEO Tags */}
        <meta
          name="keywords"
          content="Wedding, Wedding Registry, Honeymoon Fund, Wedding Planning, Guest List, Wedding Checklist, Wedding Website, Free wedding website"
        />
        <meta name="description" content={registry.greetin || defaultMetaDescription} />
        <link rel="canonical" href={window.location.href} />
        {/* Social Tags */}
        <meta
          name="twitter:title"
          property="twitter:title"
          content={`${registry.registrant_first_name} and ${registry.partner_registrant_first_name}'s Wedding Registry on Zola | Wedding Planning, Registry &amp; Gifts`}
        />
        <meta name="twitter:image" property="twitter:image" content={registry.profile_image} />
        <meta name="twitter:site" property="twitter:site" content="@zolaregistry" />
        <meta name="twitter:card" property="twitter:card" content="summary" />
        <meta
          name="twitter:description"
          property="twitter:description"
          content={registry.greeting}
        />
        <meta name="og:type" property="og:type" content="website" />
        <meta
          name="og:title"
          property="og:title"
          content={`${couplesNames}'s Wedding Registry on Zola | Wedding Planning, Registry &amp; Gifts`}
        />
        <meta
          name="og:description"
          property="og:description"
          content={registry.greeting || defaultMetaDescription}
        />
        <meta name="og:image" property="og:image" content={registry.profile_image} />
        <meta name="og:site_name" property="og:site_name" content="Zola" />
        <meta name="og:url" property="og:url" content={window.location.href} />
      </Helmet>
    );
  }

  render() {
    const {
      registry,
      match,
      otherCollections,
      selectedFacetValues,
      collectionList,
      facets,
      busy,
      user,
    } = this.props;
    const { shouldRenderPage, completeFacets, sort, resetSort, currentCategoryObj } = this.state;
    // wait till registry data loads to proceed with rendering
    if (_isUndefined(registry.public) || !shouldRenderPage) return null;
    const radioButtonsElements = ['CATEGORIES', 'SORT_BY'];
    const filterMainTitle = 'Filter & Sort';
    const logoRebrandEnabled = FeatureFlags.get('logoRebrand');
    this.handleBrowserHistory();
    return (
      <div className={cx('public-registry_container')}>
        {this.renderSEOTags()}
        <StickyContainer className="public-registry_sticky-container">
          <UnifiedNav styleOverride={{ height: 60 }} />
        </StickyContainer>
        <section className="public-registry_header-container">
          <header className="public-registry_header">{this.renderRegistryHeader()}</header>
        </section>
        <RegistryTileGrid
          // headers
          busy={busy}
          match={match}
          defaultCollection={this.getDefaultCollectionItems()}
          otherCollections={otherCollections}
          giftCardType={GIFT_CARD_TYPE}
          handleProductClick={this.handleProductClick}
          addToCart={this.addToCart}
          // tags
          selectedFacetValues={selectedFacetValues}
          selectedCollectionObjectId={currentCategoryObj?.value}
          collectionList={collectionList}
          sort={sort}
          completeFacets={completeFacets}
          handleClearAllAction={this.handleClearAllAction}
          // facets
          registry={registry}
          facets={facets}
          resetSort={resetSort}
          selectFacet={this.selectFacet}
          handleCategoryTagClick={this.handleCategoryTagClick}
          handleClearFacet={this.handleClearFacet}
          selectCategory={this.selectCategory}
          handleDropdownSelection={this.handleDropdownSelection}
          selectSortBy={this.selectSortBy}
          radioButtonsElements={radioButtonsElements}
          filterMainTitle={filterMainTitle}
          enableCustomCollections
          isReduced={
            isMobile() && experimentFlags.isUnderTest(REGISTRY_GIFT_GIVER_TILE_HEIGHT, user)
          }
          hideGaleryBadge={experimentFlags.isUnderTest(REGISTRY_GIFT_GIVER_PDP_BADGE, user)}
        />
        <Footer logoRebrand={logoRebrandEnabled} />
        <button
          id="back-to-top-button"
          aria-label="Back To Top"
          ref={this.scrollButton}
          onClick={PublicRegistry.scrollToTop}
        >
          <i className="zolaicon-chevron-up" />
        </button>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  defaultCollection: state.registryCollection.defaultCollection,
  busy: state.registryCollection.busy,
  otherCollections: state.registryCollection.otherCollections,
  collectionList: state.registryCollection.collectionList,
  facets: state.registryCollection.facets,
  selectedFacetValues: state.registryCollection.selectedFacetValues,
  registry: state.registry,
  modalInfo: state.modal,
  defaultGiftCard: state.product.defaultGiftCard,
  user: state.user.userContext,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      handleRegistrySearch: getRegistryProducts,
      handleFacetValueChange: toggleFacetValue,
      handleAllFacetValueReset: resetAllFacetValue,
      handleFacetValueReset: resetFacetValue,
      requestRegistryInfo: requestRegistryInfoBySlug,
      fetchProductByLookKey: requestProductByLookKey,
      handleAddItem: addItem,
      handleModal: showModal,
      handleHideModal: hideModal,
      handleGuestVisit: onIdentifyGuestUser,
    },
    dispatch
  );

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PublicRegistry));
